import { lazy, Suspense, useCallback, useState } from 'react';
import styled from 'styled-components';
import { HuddleHousehold, OrgId, OrgType, Role } from '../../../../../common/src/model.ts';
import { Spinner } from '../../../util/spinner.tsx';
import { PanelContent, PanelTitle } from '../org/dashboard/shared.tsx';
import { useHuddleOrgDashboard } from './use_huddle.ts';

// The usage panels are shared with /dashboard, and they pull in Chart.js, which is far too large to
// put in the main bundle for a page most of the app never opens - hence the lazy import, matching
// what dashboard.tsx does.  If the chunk fails to load the panels are simply omitted: the huddle is
// a page people read at 8 AM, and half a dashboard beats a blank screen.
const UsageStatsPanels = lazy(async () => {
  try {
    const module = await import('../org/dashboard/usage_section.tsx');
    return { default: module.UsageStatsPanels };
  } catch {
    return { default: () => <></> };
  }
});

/**
 * The active church's dashboard, embedded under its card: the same household and usage figures its
 * own admins see at /dashboard.
 *
 * Read-only, and deliberately so - the data behind it (HuddleOrgDashboard) carries no household or
 * user tokens, so there is nothing here to change a role with.  Roles are shown, not edited; that
 * still happens on /dashboard.
 */
export const HuddleOrgDashboardSection = ({
  orgId,
  orgName,
  orgType,
}: {
  orgId: OrgId;
  orgName: string;
  orgType: OrgType;
}) => {
  const { dashboard, isLoading, error } = useHuddleOrgDashboard(orgId);
  const [isExpanded, setIsExpanded] = useState(true);

  const onToggle = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  return (
    <Section>
      <SectionHeading onClick={onToggle} aria-expanded={isExpanded}>
        <Chevron $isExpanded={isExpanded} />
        <SectionTitle>{orgName} dashboard</SectionTitle>
      </SectionHeading>
      {isExpanded && (
        <Content>
          {isLoading && <Spinner />}
          {/* the cards are already on screen by now: a failure here must cost the reader nothing else */}
          {!isLoading && error && <Message>Dashboard unavailable: {error}</Message>}
          {!isLoading && !error && dashboard && (
            <>
              <Suspense fallback={null}>
                <UsageStatsPanels
                  householdCounts={dashboard.householdInfo}
                  usageInfo={dashboard.usageInfo}
                  orgType={orgType}
                />
              </Suspense>
              <Panel>
                <PanelTitle>Households</PanelTitle>
                <PanelContent>
                  <HouseholdList households={dashboard.householdInfo.households} />
                </PanelContent>
              </Panel>
            </>
          )}
        </Content>
      )}
    </Section>
  );
};

const HouseholdList = ({ households }: { households: HuddleHousehold[] }) => {
  if (households.length === 0) {
    return <Message>No households</Message>;
  }
  return (
    <Households>
      {households.map((household, index) => (
        // Keyed by position on purpose.  The household token would be the natural key, but it is a
        // bearer credential and so is deliberately absent from HuddleOrgDashboard, and surnames
        // collide often enough that the org dashboard reports uniqueLastNames as a statistic.  The
        // list is read-only and never reorders, so position is stable for as long as it is rendered.
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Household key={index}>
          <FamilyName>
            {household.familyLastName}
            {household.householdEmail && <Detail>({household.householdEmail})</Detail>}
          </FamilyName>
          {household.members.map((member, memberIndex) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Member key={memberIndex} $isStaff={!!member.roles?.length}>
              {member.name}
              {member.email && <Detail>{member.email}</Detail>}
              {member.roles?.map((role) => (
                <RoleTag key={role}>{roleLabel(role)}</RoleTag>
              ))}
            </Member>
          ))}
        </Household>
      ))}
    </Households>
  );
};

function roleLabel(role: Role): string {
  return role === Role.Administrator ? 'admin' : 'staff';
}

const Section = styled.div`
  margin-top: 8px;
  text-align: left;
  border: 1px solid var(--color-hairline);
  border-radius: 10px;
  padding: 12px 16px;
`;

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const SectionTitle = styled.div`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.1rem;
`;

// drawn in CSS rather than imported from assets/: the huddle page is covered by render tests, and
// the vitest client project has no svgr plugin, so an imported SVG cannot be mounted in one
const Chevron = styled.span<{ $isExpanded: boolean }>`
  width: 0;
  height: 0;
  flex-shrink: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 7px solid var(--color-text);
  transform: rotate(${(props) => (props.$isExpanded ? '90deg' : '0deg')});
  transition: transform 100ms;
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 5vw;
  margin-top: 12px;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Households = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 40vh;
  overflow-y: auto;
  font-size: 0.9rem;
`;

const Household = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2px 0;
`;

const FamilyName = styled.div`
  display: flex;
  gap: 6px;
  align-items: baseline;
`;

const Member = styled.div<{ $isStaff: boolean }>`
  display: flex;
  gap: 6px;
  align-items: baseline;
  padding-left: 20px;
  ${(props) => (props.$isStaff ? 'color: var(--color-dashboard-staff)' : '')};
`;

const Detail = styled.span`
  color: var(--color-text-darker);
  font-size: 0.85em;
`;

const RoleTag = styled.span`
  border: 1px solid var(--color-hairline);
  border-radius: 8px;
  padding: 0 6px;
  font-size: 0.75em;
  color: var(--color-text-darker);
`;

const Message = styled.div`
  color: var(--color-text-darker);
`;
